'use client';

import { useRouter } from '@bprogress/next/app';
import { useSelector } from '@xstate/store/react';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';

import { apiTrackingDiginetica } from '@/fo/api/tracking-diginetica';
import { FeatureProductTileBase } from '@/fo/feature/product/tile/base';
import IconSearchCategory from '@/fo/icons/base/icons/search-category.svg';
import IconSearchHistory from '@/fo/icons/base/icons/search-history.svg';
import IconSearchStar from '@/fo/icons/base/icons/search-star.svg';
import IconSearch from '@/fo/icons/base/icons/search.svg';
import {
  selectHistory,
  selectProducts,
  selectCategories,
  selectArticles,
  selectSuggestions,
  selectQuery,
  storeSearchSuggestion,
  historyActions,
  searchActions,
  DEFAULT_SUGGESTION_LIMIT
} from '@/fo/store/search-suggestion';
import { MappedProduct } from '@/fo/util/map-product-data';

import { InputSearch } from '../components/InputSearch/InputSearch';
import { SuggestionGroup } from '../components/SuggestionGroup/SuggestionGroup';

import { styles } from './FeatureSearchSuggestionBase.styles';

export interface FeatureSearchSuggestionBaseProps {
  isAuthorized?: boolean;
  suggestionLimit?: number;
}

export function FeatureSearchSuggestionBase({
  isAuthorized = false,
  suggestionLimit = DEFAULT_SUGGESTION_LIMIT
}: FeatureSearchSuggestionBaseProps) {
  const t = useTranslations();

  const slots = styles();

  const router = useRouter();

  useEffect(() => {
    storeSearchSuggestion.send({
      type: 'set',
      state: { suggestionLimit }
    });
  }, [suggestionLimit]);

  const query = useSelector(storeSearchSuggestion, selectQuery);
  const history = useSelector(storeSearchSuggestion, selectHistory);
  const products = useSelector(storeSearchSuggestion, selectProducts);
  const suggestions = useSelector(storeSearchSuggestion, selectSuggestions);
  const categories = useSelector(storeSearchSuggestion, selectCategories);
  const articles = useSelector(storeSearchSuggestion, selectArticles);

  const trackAutocompleteClick = async (
    item: string,
    type:
      | 'history'
      | 'categories'
      | 'clarifications'
      | 'products'
      | 'brands'
      | 'queries'
  ) => {
    apiTrackingDiginetica({
      eventType: 'AUTOCOMPLETE_CLICK',
      autocompleteItem: item,
      autocompleteItemType: type,
      originalSearchTerm: query,
      searchTerm: item,
      anyQueryWorked:
        query.trim().toLocaleLowerCase() !== item.trim().toLocaleLowerCase()
    });
  };

  const handleSearch = (query: string) => {
    if (query) {
      const encodedQuery = encodeURIComponent(query);
      router.push(`/search?q=${encodedQuery}`);
    }
  };

  const onProductClick = (
    e: React.MouseEvent<HTMLDivElement>,
    product: MappedProduct
  ) => {
    e.stopPropagation();
    trackAutocompleteClick(product.name, 'products');
    // какая-то проблема с переходом, добавил явную обертку
    router.push(product.link);
  };

  return (
    <div className={slots.base()}>
      <div className={slots.suggestions()}>
        <InputSearch onSearch={handleSearch} />

        {history.length > 0 && (
          <SuggestionGroup
            title={t('searchHistory')}
            items={history.map((item) => ({
              icon: <IconSearchHistory />,
              text: item
            }))}
            onItemClick={(item) => {
              trackAutocompleteClick(item.text, 'history');
              searchActions.search(item.text);
            }}
            onRemoveAllClick={() => {
              historyActions.clear();
            }}
            onRemoveItemClick={(item) => {
              historyActions.removeItem(item.text);
            }}
          />
        )}

        {suggestions.length > 0 && (
          <SuggestionGroup
            title={t('searchPopularQueries')}
            items={suggestions.map((item) => ({
              icon: <IconSearch />,
              text: item.st
            }))}
            onItemClick={(item) => {
              trackAutocompleteClick(item.text, 'queries');
              searchActions.search(item.text);
            }}
          />
        )}

        {categories.length > 0 && (
          <SuggestionGroup
            title={t('searchCategories')}
            items={categories.map((item) => ({
              icon: <IconSearchCategory />,
              text: item.name
            }))}
            onItemClick={(item) => {
              trackAutocompleteClick(item.text, 'categories');
              const encodedQuery = encodeURIComponent(item.text);
              router.push(`/search?q=${encodedQuery}`);
            }}
          />
        )}

        {articles.length > 0 && (
          <SuggestionGroup
            title={t('searchPopularArticles')}
            items={articles.map((item) => ({
              icon: <IconSearchStar />,
              text: item.name,
              url: item.link_url
            }))}
            onItemClick={(item) => {
              trackAutocompleteClick(item.text, 'clarifications');
            }}
          />
        )}
      </div>

      <div className={slots.products()}>
        <div className={slots.productList()}>
          {products.map((product, index) => (
            <div
              key={`${product.sku}-${index}`}
              className={slots.productItem()}
              onClick={(e) => {
                onProductClick?.(e, product);
              }}
            >
              <FeatureProductTileBase
                isAuthorized={isAuthorized}
                data={product}
                maxOptionsVisible={1}
                maxOptionsVisibleMobile={1}
                listName="list-result-search"
                positionIndex={index}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
